import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, throwError, firstValueFrom } from 'rxjs';
import { serverErrorMessage } from '../core/utils/server-error-message';
import { environment } from '../../environments/environment';
import { LocalCacheService } from '../core/services/local-cache.service';
import { TelemetryService } from '../core/services/telemetry.service';

export interface LoginRequest {
  emailOrMobileNo: string;
  password: string;
}

export interface UserData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  token?: string;
  refreshToken?: string;
  [key: string]: any;
}

export interface LoginResponse {
  success: boolean;
  data: UserData;
  errors: any[];
  // Legacy format support
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private cache = inject(LocalCacheService);
  private telemetryService = inject(TelemetryService);
  private baseUrl = environment.apiOrigin;
  private authUrl = environment.authApiOrigin;

  login(payload: LoginRequest) {
  const url = `${this.authUrl}/login`;

  return this.http.post<any>(url, payload).pipe(
    map((res) => {
      // Check if response indicates an error (backend might return 200 with error flag)
      if (res?.success === false || res?.error === true) {
        throw {
          status: 401,
          error: {
            detail: res?.message || res?.detail || 'Invalid username or password. Please try again.'
          }
        };
      }

      const data = res?.data;

      if (data?.token) {
        this.cache.authToken = data.token;
      }
      if (data?.refreshToken) {
        this.cache.refreshToken = data.refreshToken;
      }
      if (data?.email) {
        this.cache.userEmail = data.email;
      }
      if (data?.id) {
        this.cache.userId = String(data.id);
      }
      if (data?.firstName) {
        this.cache.firstName = data.firstName;
      }
      if (data?.lastName) {
        this.cache.lastName = data.lastName;
      }
      if (data?.phoneNumber) {
        this.cache.phoneNumber = data.phoneNumber;
      }
      if (data?.organization?.name) {
        this.cache.organizationName = data.organization.name;
      }

      const displayName = [data?.firstName, data?.lastName].filter(Boolean).join(' ');
      if (displayName) {
        this.cache.displayName = displayName;
      }

      return res;
    }),
    catchError((err: any) => {
      let msg = 'Login failed. Please try again.';
      let errorType = 'generic';
      const status = err?.status || 0;
      
      // Handle different HTTP status codes
      if (status === 401) {
        msg = 'Invalid username or password. Please check and try again.';
        errorType = 'invalid_credentials';
      } else if (status === 403) {
        msg = 'Your account has been disabled. Please contact support.';
        errorType = 'account_disabled';
      } else if (status === 404) {
        msg = 'Invalid username or password. Please check and try again.';
        errorType = 'invalid_credentials';
      } else if (status === 429) {
        msg = 'Too many login attempts. Please try again later.';
        errorType = 'rate_limited';
      } else if (status >= 500) {
        msg = 'Server error. Please try again later.';
        errorType = 'server_error';
      } else if (status === 0) {
        msg = 'Network error. Please check your connection.';
        errorType = 'network_error';
      }
      
      // The server's own reason wins over the status text above. HttpErrorResponse.message is
      // Angular's transport text ("Http failure response for ..."), so it is not used here.
      msg = serverErrorMessage(err) ?? msg;
      
      const error = new Error(msg);
      (error as any).errorType = errorType;
      return throwError(() => error);
    })
  );
  }
  logout(userId: string): Promise<any> {
    const token = this.cache.authToken;
    const apiKey = environment.oasisApiKey;
    
    // Validate required parameters
    if (!token) {
      return Promise.resolve({ error: true, detail: 'Missing authentication token' });
    }
    if (!userId) {
      return Promise.resolve({ error: true, detail: 'Missing user ID' });
    }
    if (!apiKey) {
      return Promise.resolve({ status: 200, error: false, detail: 'Session cleared locally' });
    }
    const url = `${this.authUrl}/logout`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'OASIS-API-KEY': apiKey,
    };
    const payload = { user_id: userId };
    
    // Attempt backend logout, but don't fail if it errors
    // The important thing is clearing the local session (done by caller)
    return firstValueFrom(this.http.post(url, payload, { headers }))
      .catch((err) => {
        this.telemetryService.trackEvent('AUTH_WARNING', {
          message: 'Backend logout failed; session cleared locally',
          context: 'auth.service.logout',
          status: String(err?.status ?? 'unknown'),
        });
        return { status: err?.status || 500, error: false, detail: 'Session cleared locally' };
      });
  }

  /**
   * Proxy forgot-password-link request to backend
   * @param emailOrMobileNumber User's email or mobile number
   * @param oasisApiKey Optional API key override
   */
  forgotPasswordLink(emailOrMobileNumber: string, oasisApiKey?: string) {
    const apiKey = oasisApiKey || environment.oasisApiKey;
    const token = this.cache.authToken;
    
    const headers: any = {
      'Content-Type': 'application/json',
      'OASIS-API-KEY': apiKey,
    };
    
    // Add Bearer token if available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Send as query param, empty body
    const url = `${this.authUrl}/forgot-password-link?emailOrMobileNumber=${encodeURIComponent(emailOrMobileNumber)}`;
    return this.http.post<any>(url, {}, { headers });
  }

  changePassword(userId: string, password: string, oasisApiKey?: string) {
    const token = this.cache.authToken;
    const apiKey = oasisApiKey || environment.oasisApiKey;
    if (!token) {
      return throwError(() => new Error('Missing auth token.'));
    }
    if (!userId) {
      return throwError(() => new Error('Missing user id.'));
    }
    if (!apiKey) {
      return throwError(() => new Error('Missing API key.'));
    }
    
    // SECURITY: Send password in request body, NOT in URL
    // Passwords in URLs are logged by proxies, browsers, CDNs
    const url = `${this.authUrl}/change-password`;
    const headers = {
      'Content-Type': 'application/json',
      'OASIS-API-KEY': apiKey,
      'Authorization': `Bearer ${token}`,
    };
    // UserService ChangePasswordRequestDto reads only NewPassword; the user id comes from the JWT.
    const body = { newPassword: password };

    return this.http.post<any>(url, body, { headers }).pipe(
      map((res) => {
        // Check if response indicates an error (backend might return 200 with error flag)
        if (res?.success === false || res?.error === true) {
          throw {
            status: 400,
            error: {
              detail: res?.message || res?.detail || 'Change password failed.'
            }
          };
        }
        return res;
      }),
      catchError((err: any) => {
        let msg = 'Change password failed. Please try again.';
        const status = err?.status || 0;
        
        if (status === 400) msg = 'Invalid request.';
        if (status === 401) msg = 'Unauthorized.';
        if (status === 404) msg = 'User not found.';
        if (status === 500) msg = 'Internal server error.';
        // UserService sends { success, errors[], message }; older ProblemDetails bodies carry detail.
        msg = serverErrorMessage(err) ?? msg;
        
        return throwError(() => new Error(msg));
      })
    );
  }

  get token(): string | null {
    return this.cache.authToken;
  }

  clearSession() {
    this.cache.clearAuthData();
  }

  /**
   * Fetch user profile data and store in cache.
   * Call this after SSO login to populate profile fields.
   */
  fetchAndStoreProfile() {
    const url = `${this.authUrl}/me`;
    
    return this.http.get<any>(url).pipe(
      map((res) => {
        const data = res?.data || res;
        
        if (data?.email) {
          this.cache.userEmail = data.email;
        }
        if (data?.id) {
          this.cache.userId = String(data.id);
        }
        if (data?.firstName) {
          this.cache.firstName = data.firstName;
        }
        if (data?.lastName) {
          this.cache.lastName = data.lastName;
        }
        if (data?.phoneNumber) {
          this.cache.phoneNumber = data.phoneNumber;
        }
        if (data?.organization?.name) {
          this.cache.organizationName = data.organization.name;
        }

        const displayName = [data?.firstName, data?.lastName].filter(Boolean).join(' ');
        if (displayName) {
          this.cache.displayName = displayName;
        }

        return data;
      }),
      catchError((err) => {
        if (!environment.production) {
          this.telemetryService.trackEvent('AUTH_WARNING', {
            message: 'Failed to fetch authenticated user profile',
            context: 'auth.service.fetchAuthenticatedUser',
            status: String(err?.status ?? 'unknown'),
          });
        }
        return throwError(() => err);
      })
    );
  }
}
